import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';

import useCurrentUserAndSettings from 'hooks/useCurrentUserAndSettings';
import useHandleError from 'hooks/useHandleError';
import { checkStatus, put } from 'libs/apiClient';
import type { MenuContent, MenuPreferences } from 'model/Menu/types';

import { useApi } from './base-api';

const menuContentV2Key = '/api/menu-content/v2';
const menuPreferencesKey = '/api/menu-content/preferences';
const menuContentV2LSKey = 'menu-content-v2';
const menuPreferencesLSKey = 'menu-preferences';

const readCache = <T>(lsKey: string): T | undefined => {
  try {
    const raw = localStorage.getItem(lsKey);

    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
};

// Cache keys were originally unscoped, so on a shared workstation the next
// user to log in was seeded with the previous user's menu until their fetch
// resolved. Keys are now scoped by user id; drop the legacy unscoped entries
// once so stale cross-user data can't linger.
const legacyLanguageSuffixes = ['en', 'nb', 'sv', 'pl', 'fi', 'cz', 'de'];
let legacyCacheCleared = false;
const clearLegacyCache = (): void => {
  if (legacyCacheCleared) return;
  legacyCacheCleared = true;
  try {
    [menuContentV2LSKey, menuPreferencesLSKey].forEach((baseKey) => {
      localStorage.removeItem(baseKey);
      legacyLanguageSuffixes.forEach((language) =>
        localStorage.removeItem(`${baseKey}:${language}`)
      );
    });
  } catch {
    // best-effort cleanup
  }
};

/**
 * SWR hook seeded from localStorage so the new menu renders on first paint
 * instead of flashing the old menu while content/preferences are fetched — the
 * old menu caches `menu-content` the same way (see `useGetMenuContent`).
 *
 * Cache keys are scoped by `userId`, so one user's cache can never seed another
 * user's session on a shared machine. Until the user is known no cache is read
 * or written (the layout only renders with a resolved user, so in practice the
 * id is available on first render).
 *
 * `language` (when given) is folded into the SWR key so the request re-fires and
 * shows the right labels when the UI language changes — the backend returns the
 * menu already translated.
 *
 * The cache is written under both the per-language key and a language-agnostic
 * "last known" key. On first paint `i18n.language` may not yet match the user's
 * saved language (it resolves a tick later), so seeding falls back to the last
 * known menu of *any* language — the correct-language fetch then replaces it.
 */
const useCachedApi = <T>(
  url: string,
  lsKey: string,
  silentError?: boolean,
  language?: string,
  userId?: string
) => {
  clearLegacyCache();
  const swrKey = language ? `${url}?lang=${language}` : url;
  const userKey = userId ? `${lsKey}:${userId}` : undefined;
  const cacheKey = userKey && language ? `${userKey}:${language}` : userKey;
  const fallbackData = React.useMemo(
    () =>
      userKey && cacheKey
        ? (readCache<T>(cacheKey) ?? readCache<T>(userKey))
        : undefined,
    [cacheKey, userKey]
  );
  const result = useApi<T>(
    swrKey,
    fallbackData === undefined ? undefined : { fallbackData },
    silentError
  );

  React.useEffect(() => {
    if (result.data !== undefined && userKey && cacheKey) {
      try {
        localStorage.setItem(cacheKey, JSON.stringify(result.data));
        if (language) {
          // language-agnostic seed for the next cold start (see doc comment)
          localStorage.setItem(userKey, JSON.stringify(result.data));
        }
      } catch {
        // best-effort cache — ignore quota / serialization failures
      }
    }
  }, [result.data, cacheKey, userKey, language]);

  return result;
};

/**
 * Cache identity for the menu caches. While impersonating, `userId` is the
 * CUSTOMER's id but the server (deliberately) serves the SysOp's own
 * preferences — caching that under the customer's key would seed the real
 * customer with the SysOp's menu if they ever log in on the same browser.
 * Skip the cache entirely during impersonation; SWR still fetches normally.
 */
const useMenuCacheUserId = (): string | undefined => {
  const { currentUser } = useCurrentUserAndSettings();

  return currentUser?.isImpersonating ? undefined : currentUser?.userId;
};

export const useGetMenuContentV2 = () => {
  const { i18n } = useTranslation();
  const cacheUserId = useMenuCacheUserId();

  return useCachedApi<MenuContent>(
    menuContentV2Key,
    menuContentV2LSKey,
    false,
    i18n.language,
    cacheUserId
  );
};

export const useGetMenuPreferences = () => {
  const cacheUserId = useMenuCacheUserId();

  // silentError: on backends without the endpoint we quietly fall back to the old menu
  return useCachedApi<MenuPreferences>(
    menuPreferencesKey,
    menuPreferencesLSKey,
    true,
    undefined,
    cacheUserId
  );
};

/**
 * Writes preferences into the SWR cache without a network call. Used by
 * drag-reorder, which fires once per row crossed: the UI must re-render the
 * new order immediately, but the PUT is debounced to one per gesture.
 */
export const useSetMenuPreferencesCache = () => {
  const { mutate } = useSWRConfig();

  return (preferences: MenuPreferences) =>
    mutate(menuPreferencesKey, preferences, { revalidate: false });
};

export const useSaveMenuPreferences = () => {
  const { mutate } = useSWRConfig();
  const handleError = useHandleError();

  return (preferences: MenuPreferences) =>
    mutate<MenuPreferences>(
      menuPreferencesKey,
      async (data) => {
        try {
          const response = checkStatus(
            await put(menuPreferencesKey, preferences)
          );

          return (await response.json()) as MenuPreferences;
        } catch (e) {
          handleError(e);

          return data;
        }
      },
      { optimisticData: preferences, revalidate: false }
    );
};
